import { getUUID } from '../../utils/monitoring';
import {
  makeUndefinedValueNull,
  getTelcoHost,
  USER_TYPES,
  generalAdabyronBehaviour,
} from '../../utils/adaByron';
import {
  MethodType,
  UnknownObject,
  WSElementType,
  Context,
} from '../../utils/types';
import Sonda from '../../utils/sonda';
import {
  normalizeCreateCQResponse,
  normalizeGetProvisionalAgreementResponse,
} from './normalizers';

export enum PATHS {
  CREATE_CQ = 'venta/inicializar/pedido',
  CREATE_CQ_TV = 'venta/inicializar/pedido/tv',
  CREATE_CQ_AFTER_SALE = 'venta/inicializar/pedido/postventa',
  CANCEL_ALL = 'venta/finalizar/cancelar/todas',
  SELECT_ELEMENTS = 'venta/configurar/{cqId}/elegir/elementos',
  GET_FEATURES = 'venta/configurar/{cqId}/recuperar/caracteristicas',
  ADD_TO_CART = 'venta/configurar/{cqId}/agregar/alcarro',
  EXTEND_CQ = 'venta/configurar/{cqId}/ampliar',
  EXTEND_CQ_AFTER_SALE = 'venta/configurar/{cqId}/ampliar/postventa',
  EXTEND_CQ_TV = 'venta/configurar/{cqId}/ampliar/tv',
  CANCEL_CQ = 'venta/finalizar/{cqId}/cancelar',
  GENERATE_PIN = 'venta/pin/generar',
  CHECK_PIN = 'venta/pin/comprobar',
  GET_POTS = 'venta/configurar/{cqId}/recuperar/medios',
  UPDATE_CQ = 'venta/configurar/{cqId}/actualizar/info',
  UPDATE_CA = 'venta/configurar/{cqId}/actualizar/cuenta',
  GET_CQ_PRICES = 'venta/configurar/{cqId}/recuperar/precios',
  GET_LEGAL_TERMS = 'venta/finalizar/{cqId}/recuperar/legales',
  CONFIRM_CQ = 'venta/finalizar/{cqId}/confirmar',
  GET_PROVISIONAL_AGREEMENT = 'venta/finalizar/{cqId}/recuperar/precontrato',
  GET_PRODUCT_AFTER_SALE_ACTIONS = 'venta/buscar/acciones',
  GET_CUSTOMER_PRODUCTS_TO_HIRE_PRODUCT_OFFERING = 'venta/buscar/producto',
  GET_RESOURCES = 'venta/configurar/{cqId}/recuperar/recursos',
  SELECT_RESOURCES = 'venta/configurar/{cqId}/elegir/recursos',
  GET_FIRMA_OTP = 'venta/finalizar/{cqId}/recuperar/firmaotp',
}

export const createCQ = (
  data: {
    productOffering: string;
    productId?: string;
    promotion?: string;
    forzarErrorMigra?: string;
    subType?: string;
    parentProductId?: string;
    locationId?: string;
    phone?: string;
    productOfferingsToActivate?: string;
    productsToDeactivate?: string;
    customerInquirySpec?: string;
  },
  eventName = 'CQV2.INIT_CQ',
  context: Context,
  idFront = 'VT',
): Promise<unknown> => {
  const {
    productOffering,
    productId,
    promotion,
    forzarErrorMigra,
    subType,
    parentProductId,
    locationId,
    phone,
    productOfferingsToActivate,
    productsToDeactivate,
    customerInquirySpec,
  } = data;

  return generalAdabyronBehaviour({
    context,
    idFront,
    path: PATHS.CREATE_CQ,
    userTypes: [USER_TYPES.CLIENT, USER_TYPES.TGES, USER_TYPES.OPEN],
    eventName,
    queryParams: [
      { param: 'productOfferingDestino', value: productOffering },
      { param: 'productId', value: productId },
      { param: 'promocionDestino', value: promotion },
      { param: 'forzarErrorMigra', value: forzarErrorMigra },
      { param: 'subType', value: subType },
      { param: 'productIdPadre', value: parentProductId },
      { param: 'direccionInstalacion', value: locationId },
      { param: 'phone', value: phone },
      { param: 'productOfferingsToActivate', value: productOfferingsToActivate },
      { param: 'productsToDeactivate', value: productsToDeactivate },
      { param: 'customerInquirySpec', value: customerInquirySpec },
    ],
    mockedInfo: 'createCQ',
    methodType: MethodType.GET,
    onSuccessCallback: (xhr: XMLHttpRequest): UnknownObject => {
      const { response } = xhr;
      const { datos } = JSON.parse(response);
      return normalizeCreateCQResponse(datos, context);
    },
    requestHeaders: { 'Content-Type': 'application/json;charset=UTF-8' },
    telcoHostVersion: 11,
  });
};

export const initCQTV = (
  data: {
    downgrades?: Array<{
      productId: string;
    }>;
    upgrades?: Array<{
      productOfferingId: string;
      promotion?: {
        id: string;
      }
    }>;
    parentProductId?: string;
    productOfferingsToActivate?: string;
    productsToDeactivate?: string;
  },
  context: Context,
  eventName = 'CQV2.INIT_CQ_TV',
  idFront = 'VT',
): Promise<unknown> => {
  const {
    downgrades,
    parentProductId,
    upgrades,
    productOfferingsToActivate,
    productsToDeactivate,
  } = data;

  return generalAdabyronBehaviour({
    context,
    idFront,
    path: PATHS.CREATE_CQ_TV,
    userTypes: [USER_TYPES.CLIENT],
    eventName,
    methodType: MethodType.POST,
    requestObject: {
      downgrades,
      upgrades,
      parentProductId,
      productRequest: {
        productOfferingsToActivate,
        productsToDeactivate,
      }
    },
    mockedInfo: 'createCQTV',
    onSuccessCallback: (xhr: XMLHttpRequest): UnknownObject => {
      const { response } = xhr;
      const { datos } = JSON.parse(response);
      return normalizeCreateCQResponse(datos, context);
    },
    requestHeaders: { 'Content-Type': 'application/json;charset=UTF-8' },
    telcoHostVersion: 10,
  });
};

export const cancelAll = (
  context: Context,
  idFront = 'VT',
): Promise<unknown> => {
  return generalAdabyronBehaviour({
    context,
    idFront,
    path: PATHS.CANCEL_ALL,
    userTypes: [USER_TYPES.CLIENT, USER_TYPES.TGES, USER_TYPES.OPEN],
    eventName: 'CQV2.CANCEL_ALL',
    mockedInfo: 'cancelAll',
    methodType: MethodType.POST,
    requestHeaders: { 'Content-Type': 'application/json;charset=UTF-8' },
    telcoHostVersion: 10,
  });
};

export const createCQAfterSale = (
  data: {
    customerInquirySpecId?: string,
    productId?: string;
    resourceSpecId?: string;
  },
  eventName = 'CQV2.INIT_CQ_AFTER_SALE',
  context: Context,
  idFront = 'VT',
): Promise<unknown> => {
  const {
    customerInquirySpecId,
    productId,
    resourceSpecId,
  } = data;
  
  let queryParams = [
    { param: 'customerInquirySpecId', value: customerInquirySpecId },
    { param: 'productId', value: productId }
  ];

  if(resourceSpecId){
    queryParams.push({ param: 'resourceSpecId', value: resourceSpecId });
  }

  return generalAdabyronBehaviour({
    context,
    idFront,
    path: PATHS.CREATE_CQ_AFTER_SALE,
    userTypes: [USER_TYPES.CLIENT],
    eventName,
    methodType: MethodType.GET,
    queryParams,
    mockedInfo: 'createCQAfterSale',
    onSuccessCallback: (xhr: XMLHttpRequest): UnknownObject => {
      const { response } = xhr;
      const { datos } = JSON.parse(response);
      return normalizeCreateCQResponse(datos, context);
    },
    requestHeaders: { 'Content-Type': 'application/json;charset=UTF-8' },
    telcoHostVersion: 10,
  });
};

export const initCQAfterSale = (
  data: {
    customerInquirySpecId?: string,
    productId?: string;
  },
  context: Context,
  idFront = 'VT',
): Promise<unknown> => {
  return new Promise((resolve, reject) => {
    const idRequest = getUUID(idFront);
    const { processName } = getTelcoHost(context, '', [
      USER_TYPES.CLIENT,
    ]);

    const sonda = new Sonda({
      eventName: 'CQV2.GLOBAL_INIT_CQ_AFTER_SALE',
      context,
      uniqueId: idRequest,
      origin: processName,
    });

    try {
      sonda.startWS();
      createCQAfterSale(data, 'CQV2.INIT_CQ_AFTER_SALE', context, idFront)
        .then((createCQResponse) => {
          sonda.endWS(WSElementType.OK);
          resolve(createCQResponse);
        })
        .catch((createCQErr) => {
          const { response } = createCQErr;
          // CONTINGENCIA: Si hay alguna CQ en vuelo las cancelamos y volvemos a empezar
          if (response?.error?.codigoErrorNegocio === 'CONT_E_003160') {
            cancelAll(context, idFront)
              .then(() => {
                createCQAfterSale(data, 'CQV2.INIT_CQ_AFTER_SALE_RELAUNCH', context, idFront)
                  .then((initCQRelaunchResponse) => {
                    sonda.endWS(WSElementType.OK);
                    resolve(initCQRelaunchResponse);
                  })
                  .catch((initCQRelaunchErr) => {
                    sonda.endWS(WSElementType.ERROR);
                    reject(initCQRelaunchErr);
                  });
              })
              .catch((cancelAllErr) => {
                sonda.endWS(WSElementType.ERROR);
                reject(cancelAllErr);
              });
          } else {
            sonda.endWS(WSElementType.ERROR);
            reject(createCQErr);
          }
        });
    } catch (error) {
      sonda.errorJs(error);
    }
  })
    .then((initCQRes) => {
      return initCQRes;
    })
    .catch((initCQErr) => {
      throw initCQErr;
    });
};

export const initCQ = (
  data: {
    productOffering: string;
    productId?: string;
    promotion?: string;
    forzarErrorMigra?: string;
    subType?: string;
    parentProductId?: string;
    locationId?: string;
    phone?: string;
    productOfferingsToActivate?: string;
    productsToDeactivate?: string;
    customerInquirySpec?: string;
  },
  context: Context,
  idFront = 'VT',
): Promise<unknown> => {
  return new Promise((resolve, reject) => {
    const idRequest = getUUID(idFront);
    const { processName } = getTelcoHost(context, '', [
      USER_TYPES.CLIENT,
      USER_TYPES.TGES,
      USER_TYPES.OPEN,
    ]);

    const sonda = new Sonda({
      eventName: 'CQV2.GLOBAL_INIT_CQ',
      context,
      uniqueId: idRequest,
      origin: processName,
    });

    try {
      sonda.startWS();
      createCQ(data, 'CQV2.INIT_CQ', context, idFront)
        .then((createCQResponse) => {
          sonda.endWS(WSElementType.OK);
          resolve(createCQResponse);
        })
        .catch((createCQErr) => {
          const { status, response } = createCQErr;
          // CONTINGENCIA: Si hay alguna CQ en vuelo las cancelamos y volvemos a empezar
          if (
            (status === 412 && response?.error?.codigoErrorNegocio === 'TCOL-VENTAS-008-2')
            || (response?.error?.codigoErrorNegocio === 'TCOL-VENTAS-008-5')
          ) {
            cancelAll(context, idFront)
              .then(() => {
                createCQ(data, 'CQV2.INIT_CQ_RELAUNCH', context, idFront)
                  .then((initCQRelaunchResponse) => {
                    sonda.endWS(WSElementType.OK);
                    resolve(initCQRelaunchResponse);
                  })
                  .catch((initCQRelaunchErr) => {
                    sonda.endWS(WSElementType.ERROR);
                    reject(initCQRelaunchErr);
                  });
              })
              .catch((cancelAllErr) => {
                sonda.endWS(WSElementType.ERROR);
                reject(cancelAllErr);
              });
          } else {
            sonda.endWS(WSElementType.ERROR);
            reject(createCQErr);
          }
        });
    } catch (error) {
      sonda.errorJs(error);
    }
  })
    .then((initCQRes) => {
      return initCQRes;
    })
    .catch((initCQErr) => {
      throw initCQErr;
    });
};

export const selectElements = (
  data: {
    cqId: string;
    chosenActionsArray?: Array<{
      id: string;
      customerQuoteItems: Array<{
        cqsiId: string;
        clientPhoneNumberToBeAppliedTo: string;
      }>;
    }>;
    cqsiId?: string;
    clientPhoneNumberToBeAppliedTo?: string;
  },
  context: Context,
  idFront = 'VT',
): Promise<unknown> => {
  const {
    cqId,
    chosenActionsArray = [],
    cqsiId,
    clientPhoneNumberToBeAppliedTo,
  } = data;
  return generalAdabyronBehaviour({
    context,
    idFront,
    path: PATHS.SELECT_ELEMENTS,
    userTypes: [USER_TYPES.CLIENT, USER_TYPES.TGES, USER_TYPES.OPEN],
    eventName: 'CQV2.SELECT_ELEMENTS',
    urlParamsToReplace: [{ param: '{cqId}', value: cqId }],
    mockedInfo: 'selectElements',
    methodType: MethodType.POST,
    requestObject: {
      customerQuoteItems: [
        makeUndefinedValueNull({
          chosenActions: chosenActionsArray,
          cqsiId,
          clientPhoneNumberToBeAppliedTo,
        }),
      ],
    },
    requestHeaders: { 'Content-Type': 'application/json;charset=UTF-8' },
    telcoHostVersion: 10,
  });
};

export const getFeatures = (
  data: {
    cqId: string;
    chosenActionsArray?: {
      id: string;
      customerQuoteItems: {
        id: string;
      }[];
    }[];
  },
  context: Context,
  idFront = 'VT',
): Promise<unknown> => {
  const { cqId, chosenActionsArray = [] } = data;
  return generalAdabyronBehaviour({
    context,
    idFront,
    path: PATHS.GET_FEATURES,
    userTypes: [USER_TYPES.CLIENT, USER_TYPES.TGES, USER_TYPES.OPEN],
    eventName: 'CQV2.GET_FEATURES',
    urlParamsToReplace: [{ param: '{cqId}', value: cqId }],
    mockedInfo: 'getFeatures',
    methodType: MethodType.POST,
    requestObject: makeUndefinedValueNull({
      chosenActions: chosenActionsArray,
    }),
    requestHeaders: { 'Content-Type': 'application/json;charset=UTF-8' },
    telcoHostVersion: 10,
  });
};

export const addToCart = (
  data: {
    cqId: string;
    chosenActionsArray?: {
      id: string;
      clientPhoneNumberToBeAppliedTo: string;
      offeringCharacteristics: {
        id: string;
        value: string;
      }[];
    }[];
    showPrices?: boolean;
  },
  context: Context,
  idFront = 'VT',
): Promise<unknown> => {
  const { cqId, chosenActionsArray = [], showPrices = false } = data;
  return generalAdabyronBehaviour({
    context,
    idFront,
    path: PATHS.ADD_TO_CART,
    userTypes: [USER_TYPES.CLIENT, USER_TYPES.TGES, USER_TYPES.OPEN],
    eventName: 'CQV2.ADD_TO_CART',
    queryParams: [{ param: 'mostrarPrecios', value: showPrices }],
    urlParamsToReplace: [{ param: '{cqId}', value: cqId }],
    mockedInfo: 'addToCart',
    methodType: MethodType.POST,
    requestObject: makeUndefinedValueNull({
      chosenActions: chosenActionsArray,
    }),
    requestHeaders: { 'Content-Type': 'application/json;charset=UTF-8' },
    telcoHostVersion: 10,
  });
};

export const extendCQ = (
  data: {
    cqId: string;
    productOffering: string;
    promotion?: string;
    productIdDepend?: string;
  },
  context: Context,
  idFront = 'VT',
): Promise<unknown> => {
  const { cqId, productOffering, promotion, productIdDepend } = data;
  return generalAdabyronBehaviour({
    context,
    idFront,
    path: PATHS.EXTEND_CQ,
    userTypes: [USER_TYPES.CLIENT, USER_TYPES.TGES, USER_TYPES.OPEN],
    eventName: 'CQV2.EXTEND_CQ',
    queryParams: [
      {
        param: 'productOfferingId',
        value: productOffering,
      },
      { param: 'productPromotionId', value: promotion },
      { param: 'productIdDepend', value: productIdDepend },
    ],
    urlParamsToReplace: [{ param: '{cqId}', value: cqId }],
    mockedInfo: 'extendCQ',
    methodType: MethodType.GET,
    requestHeaders: { 'Content-Type': 'application/json;charset=UTF-8' },
    telcoHostVersion: 10,
  });
};

export const extendCQAfterSale = (
  data: {
    customerInquirySpecId?: string;
    productId?: string;
    cqId?: string;
  },
  context: Context,
  idFront = 'VT',
): Promise<unknown> => {
  const { cqId, customerInquirySpecId, productId } = data;

  let queryParams = [
    { param: 'productId', value: productId }
  ];

  if(customerInquirySpecId){
    queryParams.push({ param: 'customerInquirySpecId', value: customerInquirySpecId });
  }

  return generalAdabyronBehaviour({
    context,
    idFront,
    path: PATHS.EXTEND_CQ_AFTER_SALE,
    userTypes: [USER_TYPES.CLIENT, USER_TYPES.TGES, USER_TYPES.OPEN],
    eventName: 'CQV2.EXTEND_CQ_AFTER_SALE',
    queryParams,
    urlParamsToReplace: [{ param: '{cqId}', value: cqId }],
    mockedInfo: 'extendCQAfterSale',
    methodType: MethodType.GET,
    requestHeaders: { 'Content-Type': 'application/json;charset=UTF-8' },
    telcoHostVersion: 10,
  });
};

export const extendCQTV = (
  data: {
    cqId: string;
    parentProductId?: "string",
    downgrades?: [
      {
        productId: "string"
      }
    ],
    upgrades?: [
      {
        productOfferingId: "string",
        promotion?: {
          id: "string"
        }
      }
    ]
  },
  context: Context,
  idFront = 'VT',
): Promise<unknown> => {
  const { cqId, parentProductId, downgrades, upgrades } = data;
  return generalAdabyronBehaviour({
    context,
    idFront,
    path: PATHS.EXTEND_CQ_TV,
    userTypes: [USER_TYPES.CLIENT],
    eventName: 'CQV2.EXTEND_CQ_TV',
    urlParamsToReplace: [{ param: '{cqId}', value: cqId }],
    requestObject: makeUndefinedValueNull({
      downgrades,
      parentProductId,
      upgrades,
    }),
    mockedInfo: 'extendCQTV',
    methodType: MethodType.POST,
    requestHeaders: { 'Content-Type': 'application/json;charset=UTF-8' },
    telcoHostVersion: 10,
  });
};

export const cancelCQ = (
  data: {
    cqId: string;
  },
  context: Context,
  idFront = 'VT',
): Promise<unknown> => {
  const { cqId } = data;
  return generalAdabyronBehaviour({
    context,
    idFront,
    path: PATHS.CANCEL_CQ,
    userTypes: [USER_TYPES.CLIENT, USER_TYPES.TGES, USER_TYPES.OPEN],
    eventName: 'CQV2.CANCEL_CQ',
    urlParamsToReplace: [{ param: '{cqId}', value: cqId }],
    mockedInfo: 'cancelCQ',
    methodType: MethodType.POST,
    requestHeaders: { 'Content-Type': 'application/json;charset=UTF-8' },
    telcoHostVersion: 10,
  });
};

export const generatePIN = (
  data: {
    clientPhoneNumber: string;
  },
  context: Context,
  idFront = 'VT',
): Promise<unknown> => {
  const { clientPhoneNumber } = data;
  return generalAdabyronBehaviour({
    context,
    idFront,
    path: PATHS.GENERATE_PIN,
    userTypes: [USER_TYPES.CLIENT, USER_TYPES.OPEN, USER_TYPES.TGES],
    eventName: 'CQV2.GENERATE_PIN',
    queryParams: [{ param: 'numTelefono', value: clientPhoneNumber }],
    mockedInfo: 'generatePIN',
    methodType: MethodType.GET,
    requestHeaders: { 'Content-Type': 'application/json;charset=UTF-8' },
    telcoHostVersion: 10,
  });
};

export type CheckPinRequest =
  | {
      clientPhoneNumber: string;
      pinToCheck: string;
      pinRequestId: string;
      cqId: string;
    }
  | {
      clientPhoneNumber: string;
      pinToCheck: never;
      pinRequestId: never;
      cqId: string;
    }
  | {
      clientPhoneNumber: string;
      pinToCheck: string;
      pinRequestId: string;
      cqId: never;
    };

export const checkPIN = (
  data: CheckPinRequest,
  context: Context,
  idFront = 'VT',
): Promise<unknown> => {
  const { clientPhoneNumber, pinToCheck, pinRequestId, cqId } = data;
  return generalAdabyronBehaviour({
    context,
    idFront,
    path: PATHS.CHECK_PIN,
    userTypes: [USER_TYPES.CLIENT, USER_TYPES.OPEN, USER_TYPES.TGES],
    eventName: 'CQV2.CHECK_PIN',
    mockedInfo: 'checkPIN',
    methodType: MethodType.POST,
    requestObject: makeUndefinedValueNull({
      clientPhoneNumberToBeAppliedTo: clientPhoneNumber,
      pin: pinToCheck,
      pinRequestId,
      customerQuoteId: cqId,
    }),
    requestHeaders: { 'Content-Type': 'application/json;charset=UTF-8' },
    telcoHostVersion: 10,
  });
};

export const getPOTS = (
  data: {
    cqId: string;
  },
  context: Context,
  idFront = 'VT',
): Promise<unknown> => {
  const { cqId } = data;
  return generalAdabyronBehaviour({
    context,
    idFront,
    path: PATHS.GET_POTS,
    userTypes: [USER_TYPES.CLIENT, USER_TYPES.TGES, USER_TYPES.OPEN],
    eventName: 'CQV2.GET_POTS',
    urlParamsToReplace: [{ param: '{cqId}', value: cqId }],
    mockedInfo: 'getPOTS',
    methodType: MethodType.GET,
    requestHeaders: { 'Content-Type': 'application/json;charset=UTF-8' },
    telcoHostVersion: 10,
  });
};

export const updateCQ = (
  data: {
    cqId: string;
    locationId?: string;
    sms: string;
    phone: string;
    choices?: Array<{
      id: string;
      type: string;
    }>;
    riskEvaluation?: Array<{
      chosenId: string;
      id: string;
      value: string;
    }>;
  },
  context: Context,
  idFront = 'VT',
): Promise<unknown> => {
  const { cqId, locationId, sms, phone, choices = [], riskEvaluation } = data;
  return generalAdabyronBehaviour({
    context,
    idFront,
    path: PATHS.UPDATE_CQ,
    userTypes: [USER_TYPES.CLIENT, USER_TYPES.TGES, USER_TYPES.OPEN],
    eventName: 'CQV2.UPDATE_CQ_V2',
    urlParamsToReplace: [{ param: '{cqId}', value: cqId }],
    mockedInfo: 'updateCQ',
    methodType: MethodType.POST,
    requestObject: makeUndefinedValueNull({
      choices,
      contactMedias: {
        locationId,
        sms,
        telf: phone,
      },
      riskEvaluation,
    }),
    requestHeaders: { 'Content-Type': 'application/json;charset=UTF-8' },
    telcoHostVersion: 10,
  });
};

export const updateCA = (
  data: {
    cqId: string;
    customerAccount?: {
      bankEntityCode: string;
      bankOfficeCode: string;
      customerAccountId: string;
      customerAccountName: string;
      ibanCheckDigits: string;
      ibanCountryIso2Code: string;
      locationId: string;
      spanishIbanAccountNumber: string;
      spanishIbanCheckDigits: string;
    };
  },
  context: Context,
  idFront = 'VT',
): Promise<unknown> => {
  const { cqId, customerAccount = {} } = data;
  return generalAdabyronBehaviour({
    context,
    idFront,
    path: PATHS.UPDATE_CA,
    userTypes: [USER_TYPES.CLIENT, USER_TYPES.TGES, USER_TYPES.OPEN],
    eventName: 'CQV2.UPDATE_CA',
    urlParamsToReplace: [{ param: '{cqId}', value: cqId }],
    mockedInfo: 'updateCA',
    methodType: MethodType.POST,
    requestObject: makeUndefinedValueNull({
      customerAccount,
    }),
    requestHeaders: { 'Content-Type': 'application/json;charset=UTF-8' },
    telcoHostVersion: 10,
  });
};

export const getCQPrices = (
  data: {
    cqId: string;
    chosenActions?: Array<{
      id: string;
      pinRequestId: string;
      clientPhoneNumberToBeAppliedTo: string;
      pin: string;
      actionCharacteristics: Array<{
        id: string;
        value: string;
        name: string;
      }>;
    }>;
  },
  context: Context,
  idFront = 'VT',
): Promise<unknown> => {
  const { cqId, chosenActions = [] } = data;
  return generalAdabyronBehaviour({
    context,
    idFront,
    path: PATHS.GET_CQ_PRICES,
    userTypes: [USER_TYPES.CLIENT, USER_TYPES.TGES, USER_TYPES.OPEN],
    eventName: 'CQV2.GET_CQ_PRICES',
    urlParamsToReplace: [{ param: '{cqId}', value: cqId }],
    mockedInfo: 'getCQPrices',
    methodType: MethodType.POST,
    requestObject: makeUndefinedValueNull({
      chosenActions,
    }),
    requestHeaders: { 'Content-Type': 'application/json;charset=UTF-8' },
    telcoHostVersion: 10,
  });
};

export const getLegalTerms = (
  data: {
    cqId: string;
  },
  context: Context,
  idFront = 'VT',
): Promise<unknown> => {
  const { cqId } = data;
  return generalAdabyronBehaviour({
    context,
    idFront,
    path: PATHS.GET_LEGAL_TERMS,
    userTypes: [USER_TYPES.CLIENT, USER_TYPES.TGES, USER_TYPES.OPEN],
    eventName: 'CQV2.GET_LEGAL_TERMS',
    urlParamsToReplace: [{ param: '{cqId}', value: cqId }],
    mockedInfo: 'getLegalTerms',
    methodType: MethodType.GET,
    requestHeaders: { 'Content-Type': 'application/json;charset=UTF-8' },
    telcoHostVersion: 10,
  });
};

export const confirmCQ = (
  data: {
    cqId: string;
    documents?: { documentCode: string; nonCertifiedDocumentCode: string }[];
  },
  context: Context,
  idFront = 'VT',
): Promise<unknown> => {
  const { cqId, documents } = data;
  return generalAdabyronBehaviour({
    context,
    idFront,
    path: PATHS.CONFIRM_CQ,
    userTypes: [USER_TYPES.CLIENT, USER_TYPES.TGES, USER_TYPES.OPEN],
    eventName: 'CQV2.CONFIRM_CQ',
    urlParamsToReplace: [{ param: '{cqId}', value: cqId }],
    mockedInfo: 'confirmCQ',
    methodType: MethodType.POST,
    requestObject: {
      ...(documents && { documents }),
    },
    onSuccessCallback: (xhr: XMLHttpRequest): string => {
      const { response } = xhr;
      const { datos } = JSON.parse(response);
      return datos;
    },
    requestHeaders: { 'Content-Type': 'application/json;charset=UTF-8' },
    telcoHostVersion: 10,
  });
};

export const getProvisionalAgreement = (
  data: {
    cqId: string;
  },
  context: Context,
  idFront = 'VT',
): Promise<unknown> => {
  const { cqId } = data;
  return generalAdabyronBehaviour({
    context,
    idFront,
    path: PATHS.GET_PROVISIONAL_AGREEMENT,
    userTypes: [USER_TYPES.CLIENT, USER_TYPES.TGES, USER_TYPES.OPEN],
    eventName: 'CQV2.GET_PROVISIONAL_AGREEMENT',
    urlParamsToReplace: [{ param: '{cqId}', value: cqId }],
    mockedInfo: 'getProvisionalAgreement',
    methodType: MethodType.GET,
    onSuccessNormalizer: normalizeGetProvisionalAgreementResponse,
    requestHeaders: { 'Content-Type': 'application/json;charset=UTF-8' },
    telcoHostVersion: 10,
  });
};

export const getCustomerProductsToHireProductOffering = (
  data: {
    productOffering: string;
  },
  context: Context,
  idFront = 'VT',
): Promise<unknown> => {
  const { productOffering } = data;
  return generalAdabyronBehaviour({
    context,
    idFront,
    path: PATHS.GET_CUSTOMER_PRODUCTS_TO_HIRE_PRODUCT_OFFERING,
    userTypes: [USER_TYPES.CLIENT, USER_TYPES.TGES, USER_TYPES.OPEN],
    eventName: 'CQV2.GET_CUSTOMER_PRODUCTS_TO_HIRE_PRODUCT_OFFERING',
    queryParams: [{ param: 'id', value: productOffering }],
    mockedInfo: 'getCustomerProductsToHireProductOffering',
    methodType: MethodType.GET,
    requestHeaders: { 'Content-Type': 'application/json;charset=UTF-8' },
    telcoHostVersion: 10,
  });
};

export const getProductAfterSaleActions = (
  data: {
    productId: string;
  },
  context: Context,
  idFront = 'VT',
): Promise<unknown> => {
  const { productId } = data;
  return generalAdabyronBehaviour({
    context,
    idFront,
    path: PATHS.GET_PRODUCT_AFTER_SALE_ACTIONS,
    userTypes: [USER_TYPES.CLIENT],
    eventName: 'CQV2.GET_PRODUCT_AFTER_SALE_ACTIONS',
    queryParams: [{ param: 'productId', value: productId }],
    mockedInfo: 'getProductAfterSaleActions',
    methodType: MethodType.GET,
    requestHeaders: { 'Content-Type': 'application/json;charset=UTF-8' },
    telcoHostVersion: 10,
  });
};

export const getSellingTypesByProductId = (
  data: { productId?: string },
  context: Context,
  idFront = 'VT',
): Promise<unknown> => {
  const { productId } = data;
  return generalAdabyronBehaviour({
    context,
    idFront,
    path: PATHS.GET_PRODUCT_AFTER_SALE_ACTIONS,
    userTypes: [USER_TYPES.CLIENT, USER_TYPES.TGES, USER_TYPES.OPEN],
    eventName: 'CQV2.GET_SELLING_TYPES_BY_PRODUCT_ID',
    queryParams: [{ param: 'productId', value: productId }],
    mockedInfo: 'getSellingTypesByProductId',
    methodType: MethodType.GET,
    onSuccessCallback: (xhr: XMLHttpRequest) => {
      const { response } = xhr;
      const { datos } = JSON.parse(response);
      return { productId, ...datos };
    },
    requestHeaders: { 'Content-Type': 'application/json;charset=UTF-8' },
    telcoHostVersion: 10,
  });
};

export const filterCustomerProductToHireWithActions = (
  data: {
    productOffering: string;
    productTypesTCOL?: string[];
  },
  context: Context,
  idFront = 'VT',
): Promise<unknown> => {
  return new Promise((resolve, reject) => {
    const idRequest = getUUID(idFront);
    const { productOffering, productTypesTCOL } = data;
    const { processName } = getTelcoHost(context, '', [
      USER_TYPES.CLIENT,
      USER_TYPES.TGES,
      USER_TYPES.OPEN,
    ]);
    const sonda = new Sonda({
      eventName: 'CQV2.FILTER_CUSTOMER_PRODUCT_TO_HIRE_WITH_ACTIONS',
      context,
      uniqueId: idRequest,
      origin: processName,
    });
    try {
      sonda.startWS();
      getCustomerProductsToHireProductOffering(
        { productOffering },
        context,
        idFront,
      )
        .then((res: any) => {
          let filteredInfo = [];
          let { products } = res;

          if (Array.isArray(productTypesTCOL) && productTypesTCOL.length > 0) {
            products = products.filter((product) =>
              productTypesTCOL.includes(product.productTypeTCOL),
            );
          }
          const promiseArray = [];

          products.forEach((product) => {
            product.choices.forEach((choice) => {
              const { id: productId } = choice;

              promiseArray.push(
                getSellingTypesByProductId({ productId }, context, idFront),
              );
            });
          });

          Promise.all(promiseArray)
            .then((results) => {
              filteredInfo = products.map((product) => {
                const { productTypeTCOL, choices } = product;
                const filteredChoices = choices.filter((choice) => {
                  return results.some(
                    // eslint-disable-next-line max-len
                    (result) =>
                      result.productId === choice.id &&
                      Array.isArray(result.choices) &&
                      result.choices.length > 0,
                  );
                });

                return {
                  productTypeTCOL,
                  choices: filteredChoices.map((choice) => {
                    const actionByProductId = results.find(
                      (result) => result.productId === choice.id,
                    );

                    return {
                      ...choice,
                      subTypeChoices: actionByProductId.choices,
                    };
                  }),
                };
              });
              sonda.endWS(WSElementType.OK);
              resolve(filteredInfo);
            })
            .catch((err) => {
              reject(err);
              sonda.endWS(WSElementType.ERROR, err);
            });
        })
        .catch((err) => {
          sonda.endWS(WSElementType.ERROR, err);
          reject(err);
        });
    } catch (error) {
      sonda.errorJs(error.toString());
    }
  })
    .then((res) => {
      return res;
    })
    .catch((err) => {
      throw err;
    });
};

export const getResources = (
  data: { cqId: string },
  context: Context,
  idFront = 'VT',
): Promise<unknown> => {
  const { cqId } = data;
  return generalAdabyronBehaviour({
    context,
    idFront,
    path: PATHS.GET_RESOURCES,
    userTypes: [USER_TYPES.TGES],
    eventName: 'CQV2.GET_RESOURCES',
    urlParamsToReplace: [{ param: '{cqId}', value: cqId }],
    mockedInfo: 'getResources',
    methodType: MethodType.GET,
    requestHeaders: { 'Content-Type': 'application/json;charset=UTF-8' },
    telcoHostVersion: 10,
  });
};

export const selectResources = (
  data: {
    cqId: string;
    customerQuoteItems: {
      id: string;
      resources: {
        id: string;
        entityIdentifications: { id: string; value: string }[];
        serialNumber?: string;
      }[];
    }[];
  },
  context: Context,
  idFront = 'VT',
): Promise<unknown> => {
  const { cqId, customerQuoteItems } = data;
  return generalAdabyronBehaviour({
    context,
    idFront,
    path: PATHS.SELECT_RESOURCES,
    userTypes: [USER_TYPES.TGES],
    eventName: 'CQV2.SELECT_RESOURCES',
    urlParamsToReplace: [{ param: '{cqId}', value: cqId }],
    mockedInfo: 'selectResources',
    methodType: MethodType.POST,
    requestObject: makeUndefinedValueNull({
      customerQuoteItems,
    }),
    requestHeaders: { 'Content-Type': 'application/json;charset=UTF-8' },
    telcoHostVersion: 10,
  });
};

export const getFirmaOTP = (
  data: {
    phoneNumber: string;
    cqId: string;
  },
  context: Context,
  idFront = 'VT',
): Promise<unknown> => {
  const { phoneNumber, cqId } = data;
  return generalAdabyronBehaviour({
    context,
    idFront,
    path: PATHS.GET_FIRMA_OTP,
    userTypes: [USER_TYPES.CLIENT, USER_TYPES.TGES, USER_TYPES.OPEN],
    eventName: 'CQV2.GET_FIRMA_OTP',
    mockedInfo: 'getFirmaOTP',
    methodType: MethodType.POST,
    requestObject: {
      phoneNumber: phoneNumber || '',
    },
    urlParamsToReplace: [{ param: '{cqId}', value: cqId }],
    requestHeaders: { 'Content-Type': 'application/json;charset=UTF-8' },
    telcoHostVersion: 10,
  });
};
